"use client";

import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { getCompanyInfo } from "@/services/companyService";
import { useTenancy } from "@/contexts/TenancyContext";

interface Team {
  id: string;
  name: string;
}

interface TeamsComboboxProps {
  selected: string[];
  setSelected: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  singleSelect?: boolean;
  onTeamSelect?: (teamId: string) => void;
  onTeamDeselect?: (teamId: string) => void;
  teams?: Team[];
  fetchTeams?: () => Promise<Team[]>;
}

interface TeamsContextType {
  teams: Team[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<Team[]>;
}

const TeamsContext = React.createContext<TeamsContextType>({
  teams: [],
  loading: false,
  error: null,
  refresh: async () => [],
});

export const TeamsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [teams, setTeams] = React.useState<Team[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const {activeTenancy} = useTenancy();

  const fetchTeams = async (): Promise<Team[]> => {
    try {
      setLoading(true);
      const info = await getCompanyInfo(activeTenancy.id);
      interface TeamData {
        name: string;
        dateAdded?: any;
        lastUpdated?: any;
        lastUpdatedBy?: string;
      }
      
      const teamNames = (info.customerOrg?.teams?.teamNames || {}) as Record<string, TeamData>;
      const teamList = Object.entries(teamNames).map(([id, team]) => ({
        id,
        name: team.name,
      }));
      setTeams(teamList);
      return teamList;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch teams');
      setError(error);
      console.error('Error fetching teams:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!activeTenancy.id || activeTenancy.id === "" || activeTenancy.id === "Loading...") return;
    fetchTeams();
  }, [activeTenancy]);

  return (
    <TeamsContext.Provider
      value={{
        teams,
        loading,
        error,
        refresh: fetchTeams,
      }}
    >
      {children}
    </TeamsContext.Provider>
  );
};

export const useTeams = (): TeamsContextType => {
  const context = React.useContext(TeamsContext);
  if (!context) {
    throw new Error('useTeams must be used within a TeamsProvider');
  }
  return context;
};

export function TeamsCombobox({
  selected,
  setSelected,
  placeholder = "Select teams...",
  className,
  fullWidth = false,
  singleSelect = false,
  onTeamSelect,
  onTeamDeselect,
  teams: propTeams,
  fetchTeams: propFetchTeams,
}: TeamsComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const { teams: contextTeams, loading, error } = useTeams();
  const [teams, setTeams] = React.useState<Team[]>(propTeams || []);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Use propTeams if provided, otherwise use context teams
  const displayTeams = propTeams || teams.length > 0 ? teams : contextTeams;

  React.useEffect(() => {
    if (propFetchTeams) {
      propFetchTeams().then(fetchedTeams => {
        setTeams(fetchedTeams);
      });
    }
  }, [propFetchTeams]);

  const handleSelect = (teamId: string) => {
    // Find the team to get its name
    const team = displayTeams.find(t => t.id === teamId);
    if (!team) return;
    
    const teamName = team.name;
    let newSelected: string[];
    
    if (singleSelect) {
      // For single select, just replace the current selection with the new team name
      newSelected = selected[0] === teamName ? [] : [teamName];
    } else {
      // For multi-select, toggle the team name in the selection
      newSelected = selected.includes(teamName)
        ? selected.filter((name) => name !== teamName)
        : [...selected, teamName];
    }
    
    setSelected(newSelected);
    
    // Call the appropriate callbacks
    if (singleSelect && newSelected.length === 0) {
      onTeamDeselect?.(teamName);
    } else if (singleSelect) {
      onTeamSelect?.(newSelected[0]);
    } else if (selected.includes(teamName)) {
      onTeamDeselect?.(teamName);
    } else {
      onTeamSelect?.(teamName);
    }
    
    if (singleSelect) {
      setOpen(false);
    }
  };

  const handleRemove = (teamName: string) => {
    const newSelected = selected.filter((name) => name !== teamName);
    setSelected(newSelected);
    onTeamDeselect?.(teamName);
  };

  const filteredTeams = displayTeams.filter((team) =>
    team.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (error) {
    return <div className="text-destructive">Error loading teams</div>;
  }

  return (
    <div className={cn("space-y-2", className, { 'w-full': fullWidth })}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            role="combobox"
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-controls="teams-combobox-content"
            className={cn(
              "flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              {
                'text-muted-foreground': selected.length === 0,
              },
              className
            )}
          >
            <div 
              className="flex flex-wrap gap-1 max-h-20 overflow-y-auto"
              onClick={() => setOpen(true)}
            >
              {selected.length > 0 ? (
                selected.map((teamName) => {
                  // Find the team by name instead of ID
                  const team = displayTeams.find((t) => t.name === teamName);
                  if (!team) return null;
                  
                  return (
                    <HoverCard key={teamName}>
                      <HoverCardTrigger asChild>
                        <div className="flex items-center gap-1 bg-primary/10 text-primary rounded-md px-2 py-1 text-sm cursor-help">
                          <div className="flex items-center gap-2">
                            <span className="max-w-[200px] truncate text-sm">{team.name}</span>
                          </div>
                          <div
                            role="button"
                            tabIndex={0}
                            aria-label={`Remove ${team.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(teamName);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemove(teamName);
                              }
                            }}
                            className="opacity-50 hover:opacity-100 flex items-center justify-center h-4 w-4 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
                          >
                            <X className="h-3 w-3" />
                          </div>
                        </div>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80">
                        <div className="space-y-2">
                          <h4 className="font-medium leading-none">{team.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            Team ID: {team.id}
                          </p>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  );
                })
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </div>
        </PopoverTrigger>
        
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Search teams..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="border-0 focus:ring-0"
            />
            <CommandList 
              className="max-h-[400px] overflow-auto"
              onWheel={(e) => e.stopPropagation()}
            >
              {loading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading teams...
                </div>
              ) : error ? (
                <div className="py-6 text-center text-sm text-destructive">
                  Error loading teams. Please try again.
                </div>
              ) : filteredTeams.length === 0 ? (
                <CommandEmpty>No teams found.</CommandEmpty>
              ) : (
                <CommandGroup className="max-h-[300px] overflow-y-auto">
                  {filteredTeams.map((team) => {
                    const isSelected = selected.includes(team.name);
                    return (
                      <CommandItem
                        key={team.id}
                        value={team.id}
                        onSelect={() => handleSelect(team.id)} // Still pass ID to handleSelect, but it will use the name
                        className="cursor-pointer"
                      >
                        <div className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}>
                          <Check className={"h-4 w-4"} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{team.name}</span>
                          </div>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
